import { eventStore } from './eventStore';
import type { ActiveEvent, DynamicEvent } from '@/types/dynamicEvents';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

export interface PlayerActionEvent {
  type: 'product_development' | 'product_release' | 'financial' | 'business';
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'economic' | 'technology' | 'competition' | 'regulation' | 'social';
  metadata?: Record<string, unknown>;
}

class PlayerActionService {
  // Internal state for tracking actions
  private actionHistory: PlayerActionEvent[] = [];
  private actionCounter: number = 0;

  private createPlayerEvent(action: PlayerActionEvent): DynamicEvent {
    return {
      id: `player-${action.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'systemic',
      category: action.category,
      title: action.title,
      description: action.description,
      severity: action.severity,
      triggers: [], // Player actions don't need triggers
      automaticOutcome: {
        immediateEffects: {},
        marketModifiers: []
      },
      tags: action.metadata ? [JSON.stringify(action.metadata)] : []
    };
  }

  private createActiveEvent(event: DynamicEvent, gameDate?: { day: number; month: number; year: number }): ActiveEvent {
    return {
      event,
      triggeredAt: new Date(),
      gameDate: gameDate || { day: 1, month: 1, year: 2000 }, // Use provided game date or default
      gameMonth: 0, // Will be set appropriately by the event system
      status: 'resolved' // Player actions are immediately resolved
    };
  }

  private trackAction(action: PlayerActionEvent): void {
    this.actionHistory.push(action);
    this.actionCounter++;
  }

  logProductDevelopmentStart(productName: string, cost: number, techScore: number) {
    const action: PlayerActionEvent = {
      type: 'product_development',
      title: 'New Design Started',
      description: `Development of ${productName} has begun (Cost: ${formatCurrency(cost)}, Tech Score: ${techScore})`,
      severity: 'medium',
      category: 'technology',
      metadata: {
        productName,
        cost,
        techScore,
        phase: 'development_start'
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logProductRelease(productName: string, price: number, marketAppeal: number) {
    const action: PlayerActionEvent = {
      type: 'product_release',
      title: 'Product Released',
      description: `${productName} has been released to market (Price: $${price}, Market Appeal: ${marketAppeal}%)`,
      severity: 'high',
      category: 'technology',
      metadata: {
        productName,
        price,
        marketAppeal,
        phase: 'production_start'
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logDevelopmentFailure(reason: string, attemptedProductName?: string) {
    const action: PlayerActionEvent = {
      type: 'product_development',
      title: 'Development Failed',
      description: `Product development failed: ${reason}`,
      severity: 'high',
      category: 'economic',
      metadata: {
        reason,
        attemptedProductName,
        phase: 'development_failed'
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logFinancialCrisis(type: 'bankruptcy_warning' | 'bankruptcy_final', cashAmount: number, warningCount?: number) {
    const isFinal = type === 'bankruptcy_final';
    const action: PlayerActionEvent = {
      type: 'financial',
      title: isFinal ? 'Company Bankrupt' : 'Financial Warning',
      description: isFinal 
        ? `Company has been declared bankrupt (Cash: ${formatCurrency(cashAmount)})`
        : `Company facing financial difficulties (Cash: ${formatCurrency(cashAmount)}, Warning #${warningCount})`,
      severity: isFinal ? 'critical' : 'high',
      category: 'economic',
      metadata: {
        cashAmount,
        warningCount,
        bankruptcyType: type
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logCompanyFounding(companyName: string, ceoName: string) {
    const action: PlayerActionEvent = {
      type: 'business',
      title: 'Company Founded',
      description: `${companyName} was founded by ${ceoName}`,
      severity: 'medium',
      category: 'social',
      metadata: {
        companyName,
        ceoName,
        foundingDate: new Date()
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logBusinessMilestone(title: string, description: string, type: 'revenue' | 'market_share' | 'reputation', value: number) {
    const action: PlayerActionEvent = {
      type: 'business',
      title,
      description,
      severity: 'medium',
      category: 'economic',
      metadata: {
        milestoneType: type,
        value,
        achievedAt: new Date()
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  logProductUpdate(productName: string, newPrice: number) {
    const action: PlayerActionEvent = {
      type: 'product_development',
      title: 'Product Updated',
      description: `${productName} has been updated with new specifications and price (${formatCurrency(newPrice)})`,
      severity: 'medium',
      category: 'technology',
      metadata: {
        productName,
        newPrice,
        updateType: 'design_change',
        updatedAt: new Date()
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }

  /*
  logDailyUpdate(data: { revenue: number; costs: number; profit: number; netWorth: number; cash: number; date: { day: number; month: number; year: number } }) {
    const action: PlayerActionEvent = {
      type: 'financial',
      title: 'Daily Financial Update',
      description: `Revenue: ${formatCurrency(data.revenue)}, Costs: ${formatCurrency(data.costs)}, Profit: ${formatCurrency(data.profit)}`,
      severity: 'low',
      category: 'economic',
      metadata: {
        revenue: data.revenue,
        costs: data.costs,
        profit: data.profit,
        netWorth: data.netWorth,
        cash: data.cash,
        gameDate: data.date
      }
    };

    this.trackAction(action);
    const event = this.createPlayerEvent(action);
    const activeEvent = this.createActiveEvent(event);
    eventStore.addEvent(activeEvent);
  }
  */
  /**
   * Reset the service to initial state
   */
  reset(): void {
    // console.log('PlayerActionService: Resetting to initial state');
    
    // Clear action history
    this.actionHistory = [];
    
    // Reset counter
    this.actionCounter = 0;
    
    // console.log('PlayerActionService: Reset complete');
  }

}

export const playerActionService = new PlayerActionService();